import { httpClient } from "../../../lib/http/axios";
import { isHttpClientError } from "../../../lib/http/errors";
import { FulfillmentOrderUserFacingError } from "../types";
import {
  mapEnterFulfillmentWorkspace,
  mapFulfillmentTransportPoints,
  mapFulfillmentTransportResourceCandidates,
  mapFulfillmentResourceCandidates,
  mapSupplierOptions,
  type EnterResponseContract,
  type SupplierOptionsResponseContract,
  type TransportPointsResponseContract,
  type TransportResourceCandidatesResponseContract,
  type ResourceCandidatesResponseContract,
} from "../api/adapters";
import type {
  EnterFulfillmentWorkspaceResult,
  FulfillmentOrderDetail,
  FulfillmentOrderListFilters,
  FulfillmentOrderListResult,
  FulfillmentOrderSortDirection,
  FulfillmentOrderSortField,
  FulfillmentWorkspaceEditor,
  SaveFulfillmentWorkspaceRequest,
  FulfillmentSupplierOption,
  FulfillmentSupplierUsage,
  FulfillmentResourceMentionCandidate,
  FulfillmentTransportResourceCandidate,
  FulfillmentResourceMentionLevel,
  FulfillmentResourceMentionType,
  FulfillmentResourceTransportMode,
  FulfillmentTransportPoint,
  FulfillmentOrderArchive,
  ApplyFulfillmentOrderChangeRequest,
  FulfillmentOrderChangeBatch,
  GenerateSelectedFulfillmentOrdersRequest,
} from "../types";
import {
  fetchCourseRouteWorkspace,
  fetchCourseRouteAgreementDetail,
  generateLegacyCourseRouteAgreement,
  listCourseRouteWorkspaces,
  saveLegacyCourseRouteWorkspace,
  toLegacyFulfillmentEditor,
  toLegacyFulfillmentOrderDetail,
  uploadCourseRouteAgreement,
  applyLegacyCourseRouteSigningChange,
} from "./courseRouteFulfillmentRepo";

/** 校验统一成功信封。 */
function assertOk<T extends { code: string }>(response: T): T {
  if (response.code !== "OK") throw new Error(`确认件请求失败：${response.code}`);
  return response;
}

/** 从课程线路进入唯一确认件工作区。 */
export async function enterFulfillmentWorkspace(
  itineraryId: string,
): Promise<EnterFulfillmentWorkspaceResult> {
  const response = await httpClient.post<EnterResponseContract>(
    `/fulfillment-orders/itineraries/${itineraryId}/enter`,
    undefined,
    { "axios-retry": { retries: 0 } },
  );
  return mapEnterFulfillmentWorkspace(assertOk(response.data).data);
}

export type FulfillmentOrderListQuery = FulfillmentOrderListFilters & {
  page: number;
  pageSize: number;
  sortField: FulfillmentOrderSortField;
  sortDirection: FulfillmentOrderSortDirection;
};

/** 查询确认件服务端分页列表。 */
export async function listFulfillmentOrders(
  query: FulfillmentOrderListQuery,
): Promise<FulfillmentOrderListResult> {
  return listCourseRouteWorkspaces(query);
}

/** 读取工作区编辑器。 */
export async function fetchFulfillmentWorkspaceEditor(
  workspaceId: string,
): Promise<FulfillmentWorkspaceEditor> {
  return toLegacyFulfillmentEditor(await fetchCourseRouteWorkspace(workspaceId));
}

/** 通过确认件工作区代理查询大交通起终点，避免前端依赖核价功能权限。 */
export async function searchFulfillmentTransportPoints(
  workspaceId: string,
  mode: FulfillmentResourceTransportMode,
  q: string,
  limit = 20,
): Promise<FulfillmentTransportPoint[]> {
  const response = await httpClient.get<TransportPointsResponseContract>(
    `/fulfillment-orders/workspaces/${workspaceId}/transport-points`,
    { params: { mode, q: q.trim(), limit } },
  );
  return mapFulfillmentTransportPoints(assertOk(response.data).data);
}

/** 查询确认件三类资源的 SPU/SKU @ 候选。 */
export async function searchFulfillmentResourceCandidates(
  workspaceId: string,
  resourceType: FulfillmentResourceMentionType,
  level: FulfillmentResourceMentionLevel,
  q: string,
  spuId?: string | null,
  transportMode?: FulfillmentResourceTransportMode | null,
  fulfillmentSupplierId?: string | null,
  limit = 20,
  serviceDate?: string | null,
): Promise<FulfillmentResourceMentionCandidate[]> {
  const response = await httpClient.get<ResourceCandidatesResponseContract>(
    `/fulfillment-orders/workspaces/${workspaceId}/resource-candidates`,
    {
      params: {
        resource_type: resourceType,
        level,
        ...(spuId?.trim() ? { spu_id: spuId.trim() } : {}),
        ...(serviceDate ? { service_date: serviceDate } : {}),
        ...(transportMode ? { transport_mode: transportMode } : {}),
        ...(fulfillmentSupplierId?.trim()
          ? { fulfillment_supplier_id: fulfillmentSupplierId.trim() }
          : {}),
        q: q.trim(),
        limit,
      },
    },
  );
  return mapFulfillmentResourceCandidates(assertOk(response.data).data);
}

/** 按真实起终点 ID 精确查询大交通路线及 SKU。 */
export async function searchFulfillmentTransportResourceCandidates(
  workspaceId: string,
  transportMode: FulfillmentResourceTransportMode,
  fromPointId: string,
  toPointId: string,
  specKeyword?: string | null,
): Promise<FulfillmentTransportResourceCandidate[]> {
  const response = await httpClient.get<TransportResourceCandidatesResponseContract>(
    `/fulfillment-orders/workspaces/${workspaceId}/transport-resource-candidates`,
    {
      params: {
        transport_mode: transportMode,
        from_point_id: fromPointId,
        to_point_id: toPointId,
        ...(specKeyword?.trim() ? { spec_keyword: specKeyword.trim() } : {}),
      },
    },
  );
  return mapFulfillmentTransportResourceCandidates(assertOk(response.data).data);
}

/** 按 CAS revision 保存工作区草稿。 */
export async function saveFulfillmentWorkspaceDraft(
  workspaceId: string,
  request: SaveFulfillmentWorkspaceRequest,
): Promise<FulfillmentWorkspaceEditor> {
  return saveLegacyCourseRouteWorkspace(
    workspaceId,
    request.expectedUpdatedAt,
    request.lines,
  );
}

/** 原子生成确认协议，POST 禁止客户端自动重试。 */
export async function generateFulfillmentOrders(
  workspaceId: string,
  request: GenerateSelectedFulfillmentOrdersRequest,
): Promise<FulfillmentWorkspaceEditor> {
  try {
    return await generateLegacyCourseRouteAgreement(
      workspaceId,
      request.expectedUpdatedAt,
      request.lines,
      request.selectedLineNos,
      request.settlementSupplierId,
      request.replaceDraftAgreement === true,
    );
  } catch (error) {
    const validationMessage = structuredLineValidationMessage(error);
    if (validationMessage) throw new FulfillmentOrderUserFacingError(validationMessage, { cause: error });
    throw error;
  }
}

/** 从统一错误保留的底层响应中收口本域结构化行校验信息。 */
function structuredLineValidationMessage(error: unknown): string | null {
  if (!isHttpClientError(error) || error.code !== "FULFILLMENT_LINE_VALIDATION_FAILED") {
    return null;
  }
  const cause = error.cause;
  if (!cause || typeof cause !== "object") return null;
  const response = Reflect.get(cause, "response");
  if (!response || typeof response !== "object") return null;
  const payload = Reflect.get(response, "data");
  if (!payload || typeof payload !== "object") return null;
  const rawItems = Reflect.get(payload, "data");
  if (!Array.isArray(rawItems)) return null;
  const messages = rawItems.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const lineNo = Number(Reflect.get(item, "line_no"));
    const name = String(Reflect.get(item, "name") ?? "").trim() || "未命名项目";
    const reason = String(Reflect.get(item, "reason") ?? "").trim();
    if (!Number.isInteger(lineNo) || lineNo < 1 || !reason) return [];
    return [`第 ${lineNo} 行（${name}）：${reason}`];
  });
  return messages.length ? `生成前校验未通过：${messages.join("；")}` : null;
}

/** 读取单个协议详情与不可变版本历史。 */
export async function fetchFulfillmentOrderDetail(
  fulfillmentOrderId: string,
): Promise<FulfillmentOrderDetail> {
  return toLegacyFulfillmentOrderDetail(
    await fetchCourseRouteAgreementDetail(fulfillmentOrderId),
  );
}

/** 按结算或履约用途读取供应商主数据候选。 */
export async function listSupplierOptions(
  usage: FulfillmentSupplierUsage,
  q: string,
  supplierType?: string | readonly string[] | null,
  subjectNature?: FulfillmentSupplierOption["subjectNature"],
): Promise<FulfillmentSupplierOption[]> {
  const supplierTypes = Array.isArray(supplierType)
    ? [...new Set(supplierType.map((item) => item.trim()).filter(Boolean))]
    : null;
  const normalizedSupplierType = typeof supplierType === "string"
    ? supplierType.trim()
    : "";
  const response = await httpClient.get<SupplierOptionsResponseContract>(
    "/fulfillment-orders/supplier-options",
    {
      params: {
        usage,
        ...(q?.trim() ? { q: q.trim() } : {}),
        ...(supplierTypes?.length
          ? { supplier_types: supplierTypes }
          : normalizedSupplierType
            ? { supplier_type: normalizedSupplierType }
            : {}),
        ...(subjectNature ? { subject_nature: subjectNature } : {}),
        limit: 100,
      },
      ...(supplierTypes?.length ? { paramsSerializer: { indexes: null } } : {}),
    },
  );
  return mapSupplierOptions(assertOk(response.data).data);
}

/** 按协议文档修订号上传盖章归档，业务版本 ID 不参与上传并发校验。 */
export async function uploadFulfillmentOrderArchive(
  fulfillmentOrderId: string,
  documentRevision: number,
  file: File,
): Promise<FulfillmentOrderArchive> {
  const agreement = await uploadCourseRouteAgreement(
    fulfillmentOrderId,
    documentRevision,
    file,
  );
  return {
    id: `${fulfillmentOrderId}-current-archive`,
    fulfillmentOrderId,
    versionId: String(documentRevision),
    originalFileName: agreement.archiveFileName ?? file.name,
    mimeType: file.type,
    fileSize: file.size,
    fileSha256: "",
    status: "active",
    archivedAt: agreement.effectiveAt ?? agreement.updatedAt,
  };
}

/** 提交签后增减或添加录入账本，禁止客户端自动重试。 */
export async function applyFulfillmentOrderChange(
  fulfillmentOrderId: string,
  request: ApplyFulfillmentOrderChangeRequest,
): Promise<FulfillmentOrderChangeBatch> {
  return applyLegacyCourseRouteSigningChange(fulfillmentOrderId, request);
}

export type FulfillmentOrderArchiveDownload = {
  blob: Blob;
  fileName: string;
};

/** 通过后端校验过协议与附件归属的地址下载历史盖章附件。 */
export async function downloadFulfillmentOrderArchive(
  archive: FulfillmentOrderArchive,
): Promise<FulfillmentOrderArchiveDownload> {
  if (!archive.downloadUrl?.trim()) {
    throw new FulfillmentOrderUserFacingError("该盖章附件暂不可下载");
  }
  const response = await httpClient.get<Blob>(archive.downloadUrl, { responseType: "blob" });
  return { blob: response.data, fileName: archive.originalFileName };
}
